import moment from 'moment';
import {Types} from 'mongoose';
import {EventInfo, EventParticipantListItem, EventParticipantStatus, EventParticipantType} from '@/interfaces/eventInfo.interface';
import {EventTopic} from '@/interfaces/eventTopic.interface';
import {Employee} from '@/interfaces/employee.interface';
import {UpcomingEventParticipation} from '@/interfaces/eventParticipationResponse.interface';
import {isObjectId, retrieveObjectId} from '@/helpers/isObjectId.helper';
import {CustomAccessKeys} from '@/classes/clientOnly/permissionTreeResources/enums/CustomAccessKeys';
import {mailRights} from '@/helpers/send/mailRights';

moment.locale('de');

/** Populate of the overview and the single view */
export const TRAINING_POPULATE = {
    topic: {fields: ['name', 'participantTypes']},
    organizerList: {fields: ['firstName', 'lastName']},
    participantList: {fields: ['firstName', 'lastName', 'contactInformation', 'customId', 'isTeacher', 'isApplicant']},
};

export const ONLINE_CLIENTS = ['Zoom', 'Microsoft Teams', 'FaceTime', 'Jitsi'];

export const PARTICIPANT_TYPE_ITEMS: Array<{ value: EventParticipantType; text: string; icon: string }> = [
    {value: 'Employee', text: 'Mitarbeitende / Lehrkräfte', icon: 'mdi-account-tie-outline'},
    {value: 'Student', text: 'Schüler:innen', icon: 'mdi-school-outline'},
    {value: 'Parent', text: 'Eltern', icon: 'mdi-human-male-child'},
];

export const PARTICIPANT_TYPE_TEXT: Record<EventParticipantType, string> = {
    Employee: 'Mitarbeitende',
    Student: 'Schüler:in',
    Parent: 'Eltern',
};

export type ParticipantStatusKey = 'planned' | EventParticipantStatus;

export const STATUS_META: Record<ParticipantStatusKey, { text: string; tone: string; icon: string }> = {
    planned: {text: 'geplant', tone: 'tone-muted', icon: 'mdi-calendar-blank-outline'},
    invited: {text: 'eingeladen', tone: 'tone-info', icon: 'mdi-email-fast-outline'},
    confirmed: {text: 'bestätigt', tone: 'tone-good', icon: 'mdi-check-circle-outline'},
    declined: {text: 'abgesagt', tone: 'tone-bad', icon: 'mdi-close-circle-outline'},
};

export const STATUS_ITEMS: Array<{ value: ParticipantStatusKey; text: string; tone: string; icon: string }> =
    (Object.keys(STATUS_META) as ParticipantStatusKey[]).map((key) => ({value: key, ...STATUS_META[key]}));

export function idString(value: unknown): string {
    if (!value) {
        return '';
    }
    try {
        return String(retrieveObjectId(value as any) || '');
    } catch (e) {
        return '';
    }
}

export function topicOf(training: Pick<EventInfo, 'topic'> | null | undefined): EventTopic | null {
    if (!training || !training.topic || isObjectId(training.topic)) {
        return null;
    }
    return training.topic as EventTopic;
}

export function topicName(training: Pick<EventInfo, 'topic'> | null | undefined): string {
    return topicOf(training)?.name || 'Unbekannte Schulungsart';
}

export function participantsOf(training: Partial<EventInfo> | null | undefined): EventParticipantListItem[] {
    return Array.isArray(training?.participantList) ? (training as EventInfo).participantList : [];
}

export function organizerNames(training: Partial<EventInfo>): string[] {
    const names: string[] = [];
    for (const organizer of training.organizerList || []) {
        if (organizer.alternativeOrganizer) {
            names.push(organizer.alternativeOrganizer);
        } else if (organizer.organizerRef && !isObjectId(organizer.organizerRef)) {
            const employee = organizer.organizerRef as Employee;
            names.push(`${employee.firstName || ''} ${employee.lastName || ''}`.trim() || 'Unbekannt');
        }
    }
    return names;
}

export function endOf(training: Pick<EventInfo, 'startTime' | 'durationInMinutes'>): Date {
    return moment(training.startTime).add(training.durationInMinutes || 0, 'minutes').toDate();
}

export function formatDuration(minutes: number | undefined | null): string {
    if (!minutes) {
        return '';
    }
    const h = Math.floor(minutes / 60);
    const m = minutes % 60;
    if (!h) {
        return `${m} Min.`;
    }
    return m ? `${h} Std. ${m} Min.` : `${h} Std.`;
}

export function timeRange(training: Pick<EventInfo, 'startTime' | 'durationInMinutes'>): string {
    return `${moment(training.startTime).format('HH:mm')}–${moment(endOf(training)).format('HH:mm')} Uhr`;
}

export function longDate(date: Date | string): string {
    return moment(date).format('dddd, DD. MMMM YYYY');
}

export function venueSummary(training: Pick<EventInfo, 'venue'>): { icon: string; text: string; online: boolean } {
    const venue = training.venue || ({} as EventInfo['venue']);
    if (venue.isOnlineEvent) {
        return {icon: 'mdi-video-outline', text: venue.onlineClient ? `Online · ${venue.onlineClient}` : 'Online', online: true};
    }
    if (venue.isFaceToFaceEvent) {
        const place = [venue.room, venue.building, venue.city].filter(Boolean).join(', ');
        return {icon: 'mdi-map-marker-outline', text: place ? `Präsenz · ${place}` : 'Präsenz', online: false};
    }
    return {icon: 'mdi-map-marker-question-outline', text: 'Kein Ort hinterlegt', online: false};
}

export function capacityOf(training: Partial<EventInfo>): number | null {
    return typeof training.capacity === 'number' && training.capacity > 0 ? training.capacity : null;
}

/** Participants who did not decline (they occupy a spot) */
export function occupiedSpots(training: Partial<EventInfo>): number {
    return participantsOf(training).filter((p) => p.status !== 'declined').length;
}

export function freeSpots(training: Partial<EventInfo>): number | null {
    const capacity = capacityOf(training);
    return capacity === null ? null : Math.max(0, capacity - occupiedSpots(training));
}

export function attendedCount(training: Partial<EventInfo>): number {
    return participantsOf(training).filter((p) => p.hasParticipated).length;
}

export function statusKey(item: EventParticipantListItem): ParticipantStatusKey {
    return item.status && STATUS_META[item.status] ? item.status : 'planned';
}

export function participantName(item: EventParticipantListItem): string {
    if (!item.participant || isObjectId(item.participant)) {
        return 'Unbekannt (keine Leserechte)';
    }
    const person = item.participant as { firstName?: string; lastName?: string };
    return `${person.firstName || ''} ${person.lastName || ''}`.trim() || 'Unbekannt';
}

export function participantMail(item: EventParticipantListItem): string[] {
    if (!item.participant || isObjectId(item.participant)) {
        return [];
    }
    const info = (item.participant as { contactInformation?: { mail1?: string; mail2?: string } }).contactInformation;
    return [info?.mail1, info?.mail2].filter((el): el is string => !!el);
}

export function isApplicantParticipant(item: EventParticipantListItem): boolean {
    return item.type === 'Employee' && !!item.participant && !isObjectId(item.participant) && !!(item.participant as Employee).isApplicant;
}

export function participantPath(item: EventParticipantListItem): string {
    const id = idString(item.participant);
    switch (item.type) {
        case 'Employee':
            if (isApplicantParticipant(item)) {
                return `/recruiter/applicants/${id}`;
            }
            if (item.participant && !isObjectId(item.participant) && (item.participant as Employee).isTeacher) {
                return `/teachers/${id}`;
            }
            return `/employees/${id}`;
        case 'Parent':
            return `/parents/${id}`;
        case 'Student':
            return `/students/${id}`;
        default:
            return '';
    }
}

/** Last invitation: new field invitedAt, falls back to the older lastMessage */
export function invitedAtOf(item: EventParticipantListItem): Date | null {
    return item.invitedAt || item.lastMessage || null;
}

function csvCell(value: string): string {
    return /[";\n]/.test(value) ? `"${value.replace(/"/g, '""')}"` : value;
}

export function participantsCsv(items: EventParticipantListItem[]): string {
    const header = ['Name', 'Art', 'Kunden-Nr.', 'E-Mail', 'Status', 'Teilgenommen', 'Eingeladen am'];
    const rows = items.map((item) => {
        const invited = invitedAtOf(item);
        const customId = item.participant && !isObjectId(item.participant) ? (item.participant as { customId?: string | number }).customId : '';
        return [
            participantName(item),
            PARTICIPANT_TYPE_TEXT[item.type] || item.type,
            customId ? String(customId) : '',
            participantMail(item)[0] || '',
            STATUS_META[statusKey(item)].text,
            item.hasParticipated ? 'ja' : 'nein',
            invited ? moment(invited).format('DD.MM.YYYY HH:mm') : '',
        ].map(csvCell).join(';');
    });
    return [header.join(';'), ...rows].join('\n');
}

export function downloadText(fileName: string, content: string, type = 'text/csv;charset=utf-8') {
    // BOM so that Excel recognizes UTF-8 (umlauts)
    const blob = new Blob(['﻿', content], {type});
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = fileName;
    document.body.appendChild(a);
    a.click();
    a.remove();
    URL.revokeObjectURL(url);
}

export function openMailto(mails: string[]) {
    const a = document.createElement('a');
    a.setAttribute('href', `mailto:?bcc=${Array.from(new Set(mails)).join(';')}`);
    a.click();
    a.remove();
}

/** German message of the backend (e.g. 409 "Schulung ist ausgebucht") or the plain error message */
export function apiErrorMessage(e: unknown): string {
    const err = e as { response?: { data?: { message?: unknown } }; message?: unknown; text?: unknown };
    const message = err?.response?.data?.message || err?.text || err?.message;
    if (Array.isArray(message)) {
        return message.join(', ');
    }
    return typeof message === 'string' ? message : '';
}

export function isConflict(e: unknown): boolean {
    const err = e as { response?: { status?: number }; status?: number; statusCode?: number };
    return err?.response?.status === 409 || err?.status === 409 || err?.statusCode === 409;
}

/** Count of an id list or a number (bulk add response of old and new backend) */
export function countOf(value: unknown): number {
    if (Array.isArray(value)) {
        return value.length;
    }
    return typeof value === 'number' ? value : 0;
}

/**
 * Invitation / reminder mails of a training (event-manager remindParticipants/:id/send) need SEND_MAIL_AS_CUSTOMER only;
 * looking at them first (preview) additionally RENDER_MAIL_TEMPLATES – the dialogs check that via mailRights().canPreview.
 */
export function canSendTrainingMails(manager: { getCustom: (key: CustomAccessKeys) => unknown } | undefined): boolean {
    return mailRights(manager).canSend;
}

export type UpcomingItem = {
    eventId: string;
    name: string;
    title: string;
    startTime: Date;
    durationInMinutes: number;
    venue: string;
    online: boolean;
    status: ParticipantStatusKey;
    isCancelled: boolean;
    invitedAt: Date | null;
};

/** Normalizes an entry of GET participations/:id/upcoming (field names are read defensively) */
export function normalizeUpcoming(raw: UpcomingEventParticipation & Record<string, any>): UpcomingItem | null {
    const start = raw.startTime || raw.start;
    if (!start) {
        return null;
    }
    const topic = raw.topic && !isObjectId(raw.topic as Types.ObjectId) ? (raw.topic as EventTopic) : null;
    const rawVenue = raw.venue && typeof raw.venue === 'object' ? raw.venue : null;
    const online = !!rawVenue?.isOnlineEvent;
    let venue = rawVenue?.summary || '';
    if (!venue && rawVenue) {
        venue = venueSummary({venue: rawVenue as EventInfo['venue']}).text;
    }
    const status = raw.status && STATUS_META[raw.status as ParticipantStatusKey] ? (raw.status as ParticipantStatusKey) : 'planned';
    return {
        eventId: idString(raw.eventInfo || raw.eventId || raw._id),
        name: raw.topicName || topic?.name || 'Schulung',
        title: raw.title || '',
        startTime: start,
        durationInMinutes: raw.durationInMinutes || 0,
        venue: venue || (typeof raw.venue === 'string' ? raw.venue : ''),
        online,
        status,
        isCancelled: !!raw.isCancelled,
        invitedAt: raw.invitedAt || null,
    };
}

export function personName(person: { firstName?: string; lastName?: string } | null | undefined): string {
    return `${person?.firstName || ''} ${person?.lastName || ''}`.trim();
}
